/**
 * The screen registry: every screen ID from docs/ux-flow.md, its URL (relative to the
 * /claim basename), its rail stage and its default back target. The router, the chrome,
 * the dev drawer and the tests all read this table; screens never hard-code paths.
 * Wave 2 edits screen folders, not this file.
 */
/** The app lives under /claim (vite `base`). */
export const BASENAME = '/claim';

export type Stage = 'E' | 'D' | 'C';
export const STAGES: readonly Stage[] = ['E', 'D', 'C'];

export const SCREEN_IDS = [
  'S0b',
  'S1',
  'S2',
  'S3',
  'S3-manual',
  'S4',
  'S5',
  'S6',
  'S6-stop',
  'S7',
  'S8',
  'S9',
  'S10',
  'S11',
  'S11-handoff',
  'S12',
  'S13',
  'S14-expired',
  'S15',
] as const;
export type ScreenId = (typeof SCREEN_IDS)[number];

/** Where Back goes: a screen, the static homepage, or nowhere (arrow hidden). */
export type BackTarget = ScreenId | 'home' | null;

export interface ScreenDef {
  id: ScreenId;
  /** Route path under /claim. null = shown in place inside `host`, never its own URL. */
  path: string | null;
  host?: ScreenId;
  /** Rail segment lit as "now"; null hides the rail (S14-expired, S15: they never saw the funnel). */
  stage: Stage | null;
  back: BackTarget;
  /** Placeholder only: the next screen on the happy path, for clicking through wave 1. */
  next?: { id: ScreenId; params?: Record<string, string> };
}

// URLs from docs/ux-flow.md §2. S6, S6-stop and S14-expired have no URL in the spec; the
// ones here are drafts (listed in the w1-02 report). S15's `p<n>` segment is one param
// (`:passenger` = "p1", "p2", …) because React Router has no partial dynamic segments.
export const SCREENS: Record<ScreenId, ScreenDef> = {
  S0b: { id: 'S0b', path: '/scan', stage: 'E', back: 'home', next: { id: 'S5' } },
  S1: { id: 'S1', path: '/route', stage: 'E', back: 'home', next: { id: 'S2' } },
  S2: { id: 'S2', path: '/date', stage: 'E', back: 'S1', next: { id: 'S3' } },
  S3: { id: 'S3', path: '/flight', stage: 'E', back: 'S2', next: { id: 'S5' } },
  'S3-manual': { id: 'S3-manual', path: null, host: 'S3', stage: 'E', back: 'S2' },
  S4: { id: 'S4', path: '/what-happened', stage: 'E', back: 'S3', next: { id: 'S6' } },
  S5: { id: 'S5', path: '/result', stage: 'E', back: 'S3', next: { id: 'S6' } },
  S6: { id: 'S6', path: '/question', stage: 'E', back: 'S5', next: { id: 'S7' } },
  'S6-stop': { id: 'S6-stop', path: '/stop', stage: 'E', back: 'S6' },
  S7: { id: 'S7', path: '/e/:id/contact', stage: 'D', back: 'S6', next: { id: 'S9' } },
  S8: { id: 'S8', path: '/e/:id/verdict', stage: 'D', back: 'S7' },
  S9: { id: 'S9', path: '/e/:id/passengers', stage: 'D', back: 'S7', next: { id: 'S10' } },
  S10: { id: 'S10', path: '/e/:id/booking', stage: 'D', back: 'S9', next: { id: 'S11' } },
  S11: {
    id: 'S11',
    path: '/e/:id/sign',
    stage: 'C',
    back: 'S10',
    next: { id: 'S13', params: { item: 'id' } },
  },
  'S11-handoff': { id: 'S11-handoff', path: null, host: 'S11', stage: 'C', back: null },
  S12: { id: 'S12', path: '/e/:id', stage: 'C', back: null },
  S13: { id: 'S13', path: '/e/:id/items/:item', stage: 'C', back: 'S12', next: { id: 'S12' } },
  'S14-expired': { id: 'S14-expired', path: '/e/:id/expired', stage: null, back: null },
  S15: { id: 'S15', path: '/c/:id/:passenger', stage: null, back: null },
};

/** Not screens, but routes: the resolver redirect behind the banner and email links (§4). */
export const RESUME_PATH = '/e/:id/resume';

/** Sample params for dev links and tests (a valid ULID, the demo item and passenger). */
export const DEMO_PARAMS = { id: '01K6Z8Q3B8J2W4X5Y6Z7ABCDEF', item: 'id', passenger: 'p1' } as const;

/** Screens with their own URL (everything except the in-place ones). */
export const ROUTED_SCREENS = SCREEN_IDS.map((id) => SCREENS[id]).filter(
  (d): d is ScreenDef & { path: string } => d.path !== null,
);
